import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * HoverTooltipWrapper - A wrapper component that shows a rich tooltip on hover
 * 
 * Features:
 * - Invisible bridge between trigger and tooltip for smooth mouse travel
 * - Tooltip stays open while hovering over it (for scrolling/reviewing)
 * - Graceful 500ms fade-out when leaving both trigger and tooltip
 * 
 * v2 Updates (Jan 25, 2026):
 * - Wider tooltip (480px) with max-height (400px) and scroll
 * - Improved bridge with trapezoid shape for better mouse travel
 * - Better viewport collision detection
 */

// Import tooltip dimensions from PartInfoTooltip if available, or use defaults
const TOOLTIP_WIDTH = 480;
const TOOLTIP_MAX_HEIGHT = 400;

const HoverTooltipWrapper = ({ children, tooltip, delay = 300, hideDelay = 500 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, triggerRect: null, showAbove: false });
  const triggerRef = useRef(null);
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const isHoveringTrigger = useRef(false);
  const isHoveringTooltip = useRef(false);
  const isHoveringBridge = useRef(false);

  const clearAllTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    // Only hide if not hovering trigger, tooltip, or bridge
    if (!isHoveringTrigger.current && !isHoveringTooltip.current && !isHoveringBridge.current) {
      clearAllTimeouts();
      setIsLeaving(true);
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsLeaving(false);
      }, hideDelay);
    }
  }, [clearAllTimeouts, hideDelay]);

  const cancelHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsLeaving(false);
  }, []);

  const showTooltip = useCallback(() => {
    cancelHide();
    isHoveringTrigger.current = true;
    
    showTimeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const tooltipWidth = TOOLTIP_WIDTH;
        const tooltipHeight = TOOLTIP_MAX_HEIGHT;
        const gap = 8; // Gap between trigger and tooltip
        
        // Calculate initial position - prefer below the trigger
        let top = rect.bottom + gap;
        let left = rect.left + (rect.width / 2) - (tooltipWidth / 2); // Center on trigger
        let showAbove = false;
        
        // Adjust if tooltip would go off right edge
        if (left + tooltipWidth > window.innerWidth - 16) {
          left = window.innerWidth - tooltipWidth - 16;
        }
        
        // Adjust if tooltip would go off left edge
        if (left < 16) {
          left = 16;
        }
        
        // If tooltip would go below viewport, show above trigger
        if (top + tooltipHeight > window.innerHeight - 16) {
          top = rect.top - tooltipHeight - gap;
          showAbove = true;
          // If still doesn't fit above, position at top of viewport
          if (top < 16) {
            top = 16;
            showAbove = false;
          }
        }
        
        setPosition({ 
          top, 
          left, 
          triggerRect: rect,
          showAbove
        });
        setIsVisible(true);
      }
    }, delay);
  }, [cancelHide, delay]);

  const handleTriggerLeave = useCallback(() => {
    isHoveringTrigger.current = false;
    // Longer delay to allow mouse to reach bridge or tooltip
    setTimeout(() => {
      scheduleHide();
    }, 150);
  }, [scheduleHide]);

  const handleTooltipEnter = useCallback(() => {
    isHoveringTooltip.current = true;
    cancelHide();
  }, [cancelHide]);

  const handleTooltipLeave = useCallback(() => {
    isHoveringTooltip.current = false;
    scheduleHide();
  }, [scheduleHide]);

  const handleBridgeEnter = useCallback(() => {
    isHoveringBridge.current = true;
    cancelHide();
  }, [cancelHide]);

  const handleBridgeLeave = useCallback(() => {
    isHoveringBridge.current = false;
    // Longer delay to allow mouse to reach tooltip
    setTimeout(() => {
      scheduleHide();
    }, 150);
  }, [scheduleHide]);

  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  // Calculate bridge dimensions - creates an invisible hover zone between trigger and tooltip
  const getBridgeStyle = () => {
    if (!isVisible || !position.triggerRect) return {};
    
    const rect = position.triggerRect;
    const gap = 8;
    // Bridge needs to be tall enough to cover the gap plus some overlap
    const bridgeHeight = gap + 24;
    
    if (position.showAbove) {
      // Bridge above trigger, connecting to tooltip above
      return {
        position: 'fixed',
        // Start from the bottom of tooltip with some overlap
        top: position.top + TOOLTIP_MAX_HEIGHT - 16,
        // Center on trigger but extend to cover tooltip
        left: Math.min(position.left - 10, rect.left - 30),
        width: Math.max(TOOLTIP_WIDTH + 20, rect.width + 60),
        height: bridgeHeight + 16, // Extra height for overlap
        zIndex: 49,
        pointerEvents: 'auto',
      };
    } else {
      // Bridge below trigger, connecting to tooltip below
      return {
        position: 'fixed',
        // Start from the bottom of trigger with some overlap above
        top: rect.bottom - 8,
        // Center on trigger but extend to cover tooltip
        left: Math.min(position.left - 10, rect.left - 30),
        width: Math.max(TOOLTIP_WIDTH + 20, rect.width + 60),
        height: bridgeHeight,
        zIndex: 49,
        pointerEvents: 'auto',
      };
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={handleTriggerLeave}
        className="cursor-pointer"
      >
        {children}
      </div>
      
      {isVisible && createPortal(
        <>
          {/* Invisible bridge between trigger and tooltip */}
          <div
            style={getBridgeStyle()}
            onMouseEnter={handleBridgeEnter}
            onMouseLeave={handleBridgeLeave}
            // Uncomment to debug bridge position:
            // className="bg-red-500/20"
          />
          
          {/* Tooltip container */}
          <div
            className={`fixed transition-opacity duration-300 ${
              isLeaving ? 'opacity-0' : 'opacity-100'
            }`}
            style={{ 
              top: position.top, 
              left: position.left,
              zIndex: 50,
            }}
            onMouseEnter={handleTooltipEnter}
            onMouseLeave={handleTooltipLeave}
          >
            {tooltip}
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default HoverTooltipWrapper;
